import React from 'react';
import { Flex } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';
import { ISubmissionMetadata } from '@publishing-engine/domain/models';
import { generateValidationSchema } from 'src/utils/validation';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FormFieldAITools } from '@publishing-engine/domain/types/article';
import AITools from 'src/pages/Organization/public/Journal/SubmissionUpload/components/AITools';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';

export type IAIToolValue = {
  metadata: {
    aiTools: ISubmissionMetadata['aiTools'];
  };
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: IAIToolValue) => void;
  formField: FormFieldAITools;
  value?: ISubmissionMetadata['aiTools'];
}

const AIToolsUpdateModal: React.FC<Props> = props => {
  const { isOpen, onClose, formField, onSubmit } = props;

  const defaultValues = _.set(
    {} as IAIToolValue,
    formField.caseDataFields.main,
    props.value
  );

  const schema = generateValidationSchema({ AITools: formField });

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const validateAndSubmit = (data: IAIToolValue) => {
    if (!validateAIToolsData(data)) {
      scrollToFirstError();
      return;
    }
    onSubmit(data);
    onClose();
  };

  const validateAIToolsData = (data: IAIToolValue): boolean => {
    let isValid = true;

    // Clear any existing errors for AI Tools
    methods.clearErrors(['metadata.aiTools']);

    const aiTools = data?.metadata?.aiTools;
    const exists = aiTools?.exists;
    const value = aiTools?.value;

    // Validation: if "exists" is true, then "value" must have at least one item
    if (exists && (!Array.isArray(value) || value.length === 0)) {
      methods.setError('metadata.aiTools', {
        type: 'manual',
        message: 'Please select at least one option.'
      });
      isValid = false;
    }

    return isValid;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(data => {
            validateAndSubmit(data);
          })}
        >
          <Flex
            padding={8}
            gap={6}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
          >
            <Flex maxHeight={700} overflow={'auto'}>
              <AITools config={formField} />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" type="submit" borderRadius="small">
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default AIToolsUpdateModal;
